import { all, call, put, takeLatest, select, delay } from 'redux-saga/effects'
import { StatusCodes } from 'http-status-codes'
import side from '../../../util/side/side'
import fetchJobsStatisticsService from '../../../service/job/fetch-statistics/fetchStatistics'
import fetchJob from '../../../service/job/fetch-job/fetchJob'
import suspendJob from '../../../service/job/suspend-job/suspendJob'
import resumeJob from '../../../service/job/resume-job/resumeJob'
import requestJobUpgrade from '../../../service/job/request-job-upgrade/requestJobUpgrade'
import {
  FETCH_STATISTICS,
  FETCH,
  RESUME,
  SUSPEND,
  REQUEST_UPGRADE,
  fetchJobs as fetchJobsActionCreator,
  fetchJobsStatistics,
  fetchJobsStatisticsFailed,
  setJobs,
  storeFetchedJobsStatistics,
  jobUpgradeRequested,
  jobUpgradeFailed,
  REFETCH,
  jobRefetched,
  refetchJob,
} from '../../actions/jobs/jobs'
import { fetchUpgradeCreditsForProductSpecification } from '../../actions/entitlement/entitlement'
import {
  setCurrentPage,
  setJobError,
  setJobsFetchingAllStatus,
  setJobSuspensionOrResumptionStatus,
  showDialog,
  SET_CURRENT_PAGE,
  SET_CURRENT_FILTER,
  SET_SEARCH_QUERY,
  REMOVE_FILTER,
  ADD_FILTER,
} from '../../actions/ui/ui'
import type {
  FetchJobsStatisticsAction,
  ResumeJobAction,
  SuspendJobAction,
  RequestJobUpgradeAction,
  RefetchJobAction,
} from '../../actions/jobs/jobs'
import fetchJobs from '../../../service/job/fetch-jobs/fetchJobs'
import { getConfig } from '../../../service/util/config/config'
import type { JobsStatistics } from '../../reducers/jobs/jobs'
import type { DashboardUser } from '../../../schema/user'

export const userFromStore = ({ user }: { user: DashboardUser }): DashboardUser => user

export function* fetchJobsStatisticsSaga(action: FetchJobsStatisticsAction) {
  try {
    const statistics: JobsStatistics = yield call(fetchJobsStatisticsService, action.payload)
    yield put(storeFetchedJobsStatistics(statistics))
  } catch (e) {
    yield put(fetchJobsStatisticsFailed(e.message))
  }
}

export function* toggleJobVisibilitySaga(action: SuspendJobAction | ResumeJobAction) {
  const { payload: id, type } = action
  yield put(setJobError({ id, type: 'suspensionOrResumption', value: false }))
  yield put(setJobSuspensionOrResumptionStatus({ id, isLoading: true }))
  try {
    yield call(type === SUSPEND ? suspendJob : resumeJob, id)
    yield delay(2000)
    yield put(fetchJobsActionCreator())
  } catch (error) {
    yield put(setJobError({ id, type: 'suspensionOrResumption', value: true }))
  }
  yield put(setJobSuspensionOrResumptionStatus({ id, isLoading: false }))
}

export function* fetchJobsSaga() {

  const { JOB_PAGE_SIZE } = yield getConfig()
  const {
    ui: {
      job: { currentPage, currentFilters, currentSearchQuery },
    },
  } = yield select()
  try {
    const filters = currentFilters.length
      ? currentFilters.join(',')
      : 'draft,submitted,rejected,approved,published,suspended,expired'
    yield put(setJobsFetchingAllStatus(true))
    const response = yield call(fetchJobs, currentPage, filters, currentSearchQuery)

    if (response.status === StatusCodes.OK && response.data.jobs) {
      const { amounts, jobs, total: totalJobsCount } = response.data
      const totalPageCount = Math.ceil(totalJobsCount / JOB_PAGE_SIZE)
      yield put(setJobs({ jobs, filterAmounts: amounts, totalPageCount }))
      if (currentPage > 1 && currentPage > totalPageCount) yield put(setCurrentPage(1))
      const oncePublishedJobsIds = response.data.jobs
        .filter(job => ['expired', 'published', 'suspended'].includes(job.status?.value))
        .map(job => job.id)
      if (side() === 'client' && oncePublishedJobsIds.length) {
        yield put(fetchJobsStatistics(oncePublishedJobsIds))
      }
    } else {
      // Sentry.captureException(
      //   new Error(
      //     `Failed to fetch jobs, expected ${StatusCodes.OK} but got ${response.status}`, // eslint-disable-line max-len
      //   ),
      // )
      yield put(
        showDialog({
          title: 'Er ging iets mis',
          content: 'Onverwachte serverreactie ontvangen.',
        }),
      )
    }
  } catch (error) {
    // Sentry.captureException(error)
    yield put(
      showDialog({
        title: 'Er ging iets mis',
        content: 'Vacatures ophalen mislukt. Probeer het later opnieuw.',
      }),
    )
  }
  yield put(setJobsFetchingAllStatus(false))
}

export function* requestJobUpgradeSaga(action: RequestJobUpgradeAction) {
  const { payload: { jobId, upgrade, playhead } } = action
  try {
    const response = yield call(requestJobUpgrade, jobId, upgrade)

    if (response.status === StatusCodes.OK || response.status === StatusCodes.ACCEPTED) {
      yield put(jobUpgradeRequested(jobId, upgrade, playhead))
      yield delay(2000)
      yield put(refetchJob(jobId))
    } else {
      yield put(jobUpgradeFailed(jobId, upgrade, playhead))
    }
  } catch (error) {
    yield put(jobUpgradeFailed(jobId, upgrade, playhead))

    // Sentry.captureException(error)
  }
}

export function* refetchJobSaga(action: RefetchJobAction) {
  const { payload: jobId } = action
  try {
    const response = yield call(fetchJob, jobId)

    if (response.status === StatusCodes.OK) {
      yield put(jobRefetched(response.data))

      yield put(fetchUpgradeCreditsForProductSpecification(response.data.product))
    }
  } catch (error) {
    // Sentry.captureException(error)
  }
}

export default function* saga() {
  yield all([
    takeLatest(SUSPEND, toggleJobVisibilitySaga),
    takeLatest(RESUME, toggleJobVisibilitySaga),
    takeLatest(FETCH_STATISTICS, fetchJobsStatisticsSaga),
    takeLatest(
      [FETCH, SET_CURRENT_FILTER, SET_CURRENT_PAGE, ADD_FILTER, REMOVE_FILTER, SET_SEARCH_QUERY],
      fetchJobsSaga,
    ),
    takeLatest(REQUEST_UPGRADE, requestJobUpgradeSaga),
    takeLatest(REFETCH, refetchJobSaga),
  ])
}
